import InputSlider from '@/components/sections/builder/settings/input-slider';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type AnimationStyleType = {
  overrideThemeAnimation: boolean;
  animationDelay: number;
  animationType: string;
};

export const defaultAnimationSetting: AnimationStyleType = {
  overrideThemeAnimation: false,
  animationDelay: 200,
  animationType: 'fade',
};

export const getOverrideAnimationParams = (animation?: AnimationStyleType, index?: number) => {
  if (!animation) return {};
  if (!animation.overrideThemeAnimation) return {};

  return {
    'data-aos-override': 'true',
    'data-aos': animation.animationType,
    'data-aos-delay': animation.animationDelay * (index || 0),
  };
};

type AnimationSettingProps = {
  animation?: AnimationStyleType;
  onChange: (v: AnimationStyleType) => void;
};

export default function AnimationSetting({
  animation: animationData,
  onChange,
}: AnimationSettingProps) {
  const animation = animationData || defaultAnimationSetting;

  const setValue = (value: Partial<AnimationStyleType>) => {
    onChange({ ...animation, ...value });
  };

  const animationTypeOptions = [
    { value: 'fade', label: 'fade' },
    { value: 'fade-up', label: 'fade-up' },
    { value: 'fade-down', label: 'fade-down' },
    { value: 'fade-left', label: 'fade-left' },
    { value: 'fade-right', label: 'fade-right' },
    { value: 'fade-up-right', label: 'fade-up-right' },
    { value: 'fade-up-left', label: 'fade-up-left' },
    { value: 'fade-down-right', label: 'fade-down-right' },
    { value: 'fade-down-left', label: 'fade-down-left' },
    { value: 'flip-up', label: 'flip-up' },
    { value: 'flip-down', label: 'flip-down' },
    { value: 'flip-left', label: 'flip-left' },
    { value: 'flip-right', label: 'flip-right' },
    { value: 'slide-up', label: 'slide-up' },
    { value: 'slide-down', label: 'slide-down' },
    { value: 'slide-left', label: 'slide-left' },
    { value: 'slide-right', label: 'slide-right' },
    { value: 'zoom-in', label: 'zoom-in' },
    { value: 'zoom-in-up', label: 'zoom-in-up' },
    { value: 'zoom-in-down', label: 'zoom-in-down' },
    { value: 'zoom-in-left', label: 'zoom-in-left' },
    { value: 'zoom-in-right', label: 'zoom-in-right' },
    { value: 'zoom-out', label: 'zoom-out' },
    { value: 'zoom-out-up', label: 'zoom-out-up' },
    { value: 'zoom-out-down', label: 'zoom-out-down' },
    { value: 'zoom-out-left', label: 'zoom-out-left' },
    { value: 'zoom-out-right', label: 'zoom-out-right' },
  ];

  return (
    <div>
      <div className='flex gap-x-1'>
        <Checkbox
          id='overrideThemeAnimation'
          aria-label='Select'
          className='mx-2'
          checked={animation.overrideThemeAnimation}
          onCheckedChange={(checked) => {
            setValue({ overrideThemeAnimation: !!checked });
          }}
        />
        <label htmlFor='overrideThemeAnimation'>Override theme animation</label>
      </div>
      {animation.overrideThemeAnimation && (
        <>
          <div className='mb-4 mt-4'>Animation type</div>
          <div className='mb-4'>
            <Select
              name='animationType'
              value={animation.animationType}
              onValueChange={(val) => setValue({ animationType: val })}
            >
              <SelectTrigger className='py-5'>
                <SelectValue placeholder='Select' />
              </SelectTrigger>
              <SelectContent side='bottom' className='max-h-48 overflow-y-auto'>
                {animationTypeOptions.map((obj) => (
                  <SelectItem key={obj.value} value={obj.value}>
                    {obj.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <InputSlider
            label='Animation delay'
            value={animation.animationDelay || 0}
            max={600}
            min={0}
            step={100}
            onChange={(val) => setValue({ animationDelay: val })}
            rightContent={<div>ms</div>}
          />
        </>
      )}
    </div>
  );
}
